// Formant-staff, cross-org view of the robot onboarding ledger
// (RobotApprovedPlan). Read-only: GET /v1/admin/robot-plans/overview and
// GET /v1/admin/robot-plans/:id. A plan's "run" is what the persistent agent
// that asked for the approval did around it, joined through
// approvalRequestId → PersistentAgentElicitation → turns / tool calls.
// Dates on the wire are ISO-8601 strings.
import { z } from 'zod';
import { RobotApprovedPlanSchema, RobotApprovedPlanStatusSchema } from './robot-approved-plan.js';
import { RobotDeviceCategorySchema } from './robot-profile.js';
/**
 * Why a plan did not go live, classified from its receipt. `none` is a plan
 * that went live, is still in flight, or was superseded without an error.
 */
export const RobotPlanFailureCategorySchema = z.enum([
    'apply_interrupted',
    'connector_failed',
    'realtime_peer_down',
    'twist_not_received',
    'robot_did_not_move',
    'camera_failed',
    'streams_silent',
    'device_name_conflict',
    'no_receipt',
    'other',
    'none',
]);
/** Query of GET /v1/admin/robot-plans/overview. Window defaults to the last 30 days. */
export const AdminRobotPlansQuerySchema = z.object({
    since: z.string().datetime().optional(),
    until: z.string().datetime().optional(),
    orgId: z.string().optional(),
    status: RobotApprovedPlanStatusSchema.optional(),
    /** Case-insensitive match on robot name, source id, device name or plan id. */
    search: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(1000).default(500),
});
/** Cost and activity of the agent run around one plan. */
export const RobotPlanRunStatsSchema = z.object({
    turns: z.number().int(),
    toolCalls: z.number().int(),
    toolErrors: z.number().int(),
    /** Calls the agent made to the robot itself (realtime_source_call). */
    robotCalls: z.number().int(),
    usd: z.number(),
    tokensIn: z.number().int(),
    tokensOut: z.number().int(),
    /** First turn start to last turn finish. */
    wallMs: z.number().int().nullable(),
});
/** What the plan set up, read from the plan object. */
export const RobotPlanCapabilitiesSchema = z.object({
    teleop: z.boolean(),
    cameras: z.number().int(),
    commands: z.number().int(),
    streams: z.number().int(),
});
/** One plan as a dashboard row. The full plan object is only on the detail. */
export const AdminRobotPlanRowSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    robotSourceId: z.string(),
    robotName: z.string().nullable(),
    deviceId: z.string().nullable(),
    deviceName: z.string().nullable(),
    planId: z.string(),
    status: RobotApprovedPlanStatusSchema,
    approvedAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    approvedByUserId: z.string().nullable(),
    /** 1-based: this plan's position among this robot's plans in its org, oldest first. */
    attempt: z.number().int(),
    /** approvedAt → updatedAt once live or failed; null while in flight or superseded. */
    settleMs: z.number().int().nullable(),
    failureCategory: RobotPlanFailureCategorySchema,
    /** receipt.error, base64 blobs elided, at most 240 chars. */
    error: z.string().nullable(),
    flowing: z.array(z.string()),
    silent: z.array(z.string()),
    capabilities: RobotPlanCapabilitiesSchema,
    /** What it ran on; `Unknown` / null when the plan did not say. */
    deviceType: z.string(),
    deviceCategory: RobotDeviceCategorySchema.nullable(),
    os: z.string().nullable(),
    arch: z.string().nullable(),
    persistentAgentId: z.string().nullable(),
    agentName: z.string().nullable(),
    run: RobotPlanRunStatsSchema.nullable(),
});
export const AdminRobotPlanInsightsSchema = z.object({
    window: z.object({ since: z.string().datetime(), until: z.string().datetime() }),
    totals: z.object({
        plans: z.number().int(),
        robots: z.number().int(),
        orgs: z.number().int(),
        live: z.number().int(),
        failed: z.number().int(),
        /** approved or applied: waiting on apply. */
        inFlight: z.number().int(),
        superseded: z.number().int(),
        /** Robots with at least one live plan in the window. */
        robotsLive: z.number().int(),
        /** Robots whose newest plan failed and none went live. */
        robotsStuck: z.number().int(),
        /** Of robots whose first plan in the window settled, the share that went live (0..1); null when none settled. */
        firstTryLiveRate: z.number().nullable(),
        /** Mean attempt number of the first live plan per robot; null when none went live. */
        avgAttemptsToLive: z.number().nullable(),
        medianSettleMs: z.number().int().nullable(),
        p90SettleMs: z.number().int().nullable(),
        usd: z.number(),
        toolCalls: z.number().int(),
        toolErrors: z.number().int(),
    }),
    /** One entry per UTC day in the window, zero-filled. */
    daily: z.array(z.object({
        date: z.string(),
        live: z.number().int(),
        failed: z.number().int(),
        superseded: z.number().int(),
        inFlight: z.number().int(),
    })),
    failures: z.array(z.object({
        category: RobotPlanFailureCategorySchema,
        count: z.number().int(),
        robots: z.number().int(),
        lastSeenAt: z.string().datetime(),
        /** Newest error text in this category. */
        example: z.string().nullable(),
    })),
    /** Streams across settled plans, most mentioned first, at most 30. */
    streams: z.array(z.object({ name: z.string(), flowing: z.number().int(), silent: z.number().int() })),
    capabilities: z.object({
        teleop: z.number().int(),
        camera: z.number().int(),
        commands: z.number().int(),
        streamsOnly: z.number().int(),
    }),
    orgs: z.array(z.object({
        orgId: z.string(),
        plans: z.number().int(),
        live: z.number().int(),
        failed: z.number().int(),
        robots: z.number().int(),
        robotsLive: z.number().int(),
        lastApprovedAt: z.string().datetime(),
        usd: z.number(),
    })),
    stuckRobots: z.array(z.object({
        orgId: z.string(),
        robotSourceId: z.string(),
        robotName: z.string().nullable(),
        attempts: z.number().int(),
        lastPlanId: z.string(),
        lastRowId: z.string(),
        lastError: z.string().nullable(),
        lastCategory: RobotPlanFailureCategorySchema,
        lastApprovedAt: z.string().datetime(),
    })),
    /** Tools the agent used in plan runs. Robot tools appear as `robot:<name>`. Most called first, at most 25. */
    tools: z.array(z.object({
        toolName: z.string(),
        calls: z.number().int(),
        errors: z.number().int(),
        p50DurationMs: z.number().int().nullable(),
    })),
});
/** Response of GET /v1/admin/robot-plans/overview. */
export const AdminRobotPlansOverviewSchema = z.object({
    insights: AdminRobotPlanInsightsSchema,
    /** Newest first, at most `limit`. */
    plans: z.array(AdminRobotPlanRowSchema),
    /** True when more plans matched than `limit`; insights still cover all of them. */
    truncated: z.boolean(),
});
/** One tool call in a plan's run, oldest first. */
export const RobotPlanToolCallSchema = z.object({
    id: z.string(),
    at: z.string().datetime(),
    turnId: z.string().nullable(),
    toolName: z.string(),
    /** realtime_source_call's inner toolName (discover, preview, propose, apply, …). */
    robotTool: z.string().nullable(),
    /** Args JSON, truncated to 4 KB of text. */
    args: z.unknown().nullable(),
    isError: z.boolean(),
    durationMs: z.number().int().nullable(),
});
export const RobotPlanTurnSchema = z.object({
    id: z.string(),
    status: z.string(),
    objective: z.string().nullable(),
    startedAt: z.string().datetime(),
    finishedAt: z.string().datetime().nullable(),
    durationMs: z.number().int().nullable(),
    toolCalls: z.number().int(),
    toolErrors: z.number().int(),
    usd: z.number(),
    tokensIn: z.number().int(),
    tokensOut: z.number().int(),
});
/** Where a piece of the recipe was read from, most exact first. */
export const RobotPlanRecipeSourceSchema = z.enum([
    /** The stored plan object. */
    'plan',
    /** The approval question the human was shown (scripts are fenced in it verbatim). */
    'approval',
    /** The arguments of the agent's propose / Formant calls in this plan's run. */
    'tool_call',
]);
export const RobotPlanScriptKindSchema = z.enum([
    /** custom_script: the publish loop that reads the robot and pushes telemetry. */
    'telemetry',
    /** Teleop drive script: joystick twists on stdin → the robot's SDK. */
    'drive',
    /** Teleop camera script: JPEG frames on stdout. */
    'camera',
    /** Teleop command script: button presses on stdin. */
    'commands',
    'other',
]);
export const RobotPlanScriptSchema = z.object({
    kind: RobotPlanScriptKindSchema,
    /** Short title, e.g. "Telemetry script", "Camera /camera/front". */
    title: z.string(),
    /** The approval question's own label for it, when read from there. */
    note: z.string().nullable(),
    /** Fence language: bash, python, … */
    language: z.string(),
    code: z.string(),
    source: RobotPlanRecipeSourceSchema,
});
/** One call the agent made in this plan's run that ran something on the robot or wrote Formant. */
export const RobotPlanRecipeCallSchema = z.object({
    at: z.string().datetime(),
    /** `robot:<tool>` for realtime_source_call, else the Formant tool name. */
    tool: z.string(),
    /** The one line that matters: the shell command, the topic, the device name, … */
    summary: z.string().nullable(),
    /** Arguments with secrets (tokens) redacted, at most 4 KB of JSON. */
    args: z.unknown().nullable(),
    isError: z.boolean(),
});
/**
 * "How it was made to work": exactly what ran on the robot for this plan,
 * read from the stored plan, the approval question and the run's tool calls.
 * A null or empty field means it was not recorded.
 */
export const RobotPlanRecipeSchema = z.object({
    /**
     * `full`: the stored plan is the onboard-agent's propose output (it has
     * `steps`). `summary`: the agent stored its own summary, so steps and some
     * settings were not recorded.
     */
    planRecord: z.enum(['full', 'summary']),
    /** The apply steps in order, as planned. Null when not recorded. */
    steps: z
        .array(z.object({
        id: z.string().nullable(),
        action: z.string(),
        /** What this built-in action does on the robot; null for unknown actions. */
        description: z.string().nullable(),
        /** Every other key of the step (script, detach, …). */
        args: z.record(z.unknown()),
    }))
        .nullable(),
    /** The approval's one-line "how": our WebRTC peer, the python connector, the stock agent, … */
    approach: z.string().nullable(),
    scripts: z.array(RobotPlanScriptSchema),
    driver: z
        .object({
        name: z.string(),
        version: z.string().nullable(),
        build: z.string().nullable(),
        /** What the driver saw / was configured with (robot, E-Stop mode, SDK, cameras). */
        config: z.record(z.unknown()).nullable(),
        /** The approval question's lines about the driver. */
        notes: z.array(z.string()),
        source: RobotPlanRecipeSourceSchema,
    })
        .nullable(),
    /** Formant stream → where it comes from on the robot (a topic, `custom`, a script). */
    streams: z.array(z.object({
        name: z.string(),
        /** ROS topic, or null for a custom (pushed / host) stream. */
        topic: z.string().nullable(),
        /** `ros-topic`, `custom`, `host`, … when known. */
        type: z.string().nullable(),
    })),
    teleop: z
        .object({
        /** Twist topic the joystick publishes to. */
        joystick: z.string().nullable(),
        cameras: z.array(z.object({ stream: z.string(), source: z.string().nullable() })),
        buttons: z.array(z.object({ name: z.string(), target: z.string().nullable() })),
        quality: z.string().nullable(),
    })
        .nullable(),
    /** ROS / container / rosbridge settings the plan ran against. */
    runtime: z
        .object({
        ros: z.string().nullable(),
        rmw: z.string().nullable(),
        domainId: z.number().nullable(),
        user: z.string().nullable(),
        container: z.string().nullable(),
        image: z.string().nullable(),
        containerRuntime: z.string().nullable(),
        hostNetwork: z.boolean().nullable(),
        rosbridge: z.unknown().nullable(),
        transport: z.unknown().nullable(),
        envFiles: z.array(z.string()),
    })
        .nullable(),
    /** The device configuration written to Formant. */
    formantDocument: z
        .object({ document: z.record(z.unknown()), source: RobotPlanRecipeSourceSchema })
        .nullable(),
    /** Robot tool calls (run, preview, propose, apply, …) and Formant writes, oldest first. */
    calls: z.array(RobotPlanRecipeCallSchema),
});
/** Response of GET /v1/admin/robot-plans/:id. */
export const AdminRobotPlanDetailSchema = z.object({
    row: AdminRobotPlanRowSchema,
    plan: RobotApprovedPlanSchema,
    approval: z
        .object({
        id: z.string(),
        question: z.string(),
        status: z.string(),
        /** PersistentAgentElicitation.decision as stored. */
        decision: z.unknown().nullable(),
        decidedBySubject: z.string().nullable(),
        askedAt: z.string().datetime(),
        decidedAt: z.string().datetime().nullable(),
    })
        .nullable(),
    /** Console deep link into the agent's transcript, when the run is known. */
    transcript: z.object({ agentId: z.string(), threadId: z.string() }).nullable(),
    turns: z.array(RobotPlanTurnSchema),
    toolCalls: z.array(RobotPlanToolCallSchema),
    /** Every plan for this robot in this org, oldest first (includes this one). */
    attempts: z.array(AdminRobotPlanRowSchema),
    /** What ran on the robot, for the "How it was made to work" section. */
    recipe: RobotPlanRecipeSchema,
});
/** Query of GET /v1/admin/robot-plans/devices. Window defaults to the last 30 days. */
export const AdminRobotPlanDevicesQuerySchema = z.object({
    since: z.string().datetime().optional(),
    until: z.string().datetime().optional(),
    orgId: z.string().optional(),
    /** Most recent failed plans to list, 1..200, default 50. */
    failures: z.coerce.number().int().min(1).max(200).default(50),
});
/** The ways the Devices pie can slice onboardings. */
export const RobotPlanDeviceDimensionSchema = z.enum([
    'deviceType',
    'deviceCategory',
    'os',
    'arch',
]);
/** Onboardings sharing one value of a dimension (`Unknown` when not recorded). */
export const RobotPlanDeviceSliceSchema = z.object({
    key: z.string(),
    plans: z.number().int(),
    robots: z.number().int(),
    live: z.number().int(),
    failed: z.number().int(),
    /** approved or applied: waiting on apply. */
    inFlight: z.number().int(),
    superseded: z.number().int(),
    /** Robots with at least one live plan. */
    robotsLive: z.number().int(),
});
export const RobotPlanDeviceFailureSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    robotSourceId: z.string(),
    robotName: z.string().nullable(),
    approvedAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    deviceType: z.string(),
    deviceCategory: RobotDeviceCategorySchema.nullable(),
    os: z.string().nullable(),
    osVersion: z.string().nullable(),
    arch: z.string().nullable(),
    simulated: z.boolean().nullable(),
    failureCategory: RobotPlanFailureCategorySchema,
    /**
     * The apply step (or check) that failed, e.g. install_realtime: the
     * reported `failedStep`, else read from the receipt; null when nothing says.
     */
    step: z.string().nullable(),
    /** The robot's problem code when the agent reported one, e.g. realtime_missing. */
    errorCode: z.string().nullable(),
    /** One line: base64 elided, at most 240 chars. */
    error: z.string().nullable(),
});
/** Response of GET /v1/admin/robot-plans/devices. */
export const AdminRobotPlanDevicesSchema = z.object({
    window: z.object({ since: z.string().datetime(), until: z.string().datetime() }),
    totals: z.object({
        plans: z.number().int(),
        robots: z.number().int(),
        live: z.number().int(),
        failed: z.number().int(),
        /** Plans whose robot reported a robotProfile (the rest are inferred or Unknown). */
        profiled: z.number().int(),
    }),
    /** One list per dimension, most plans first. */
    slices: z.object({
        deviceType: z.array(RobotPlanDeviceSliceSchema),
        deviceCategory: z.array(RobotPlanDeviceSliceSchema),
        os: z.array(RobotPlanDeviceSliceSchema),
        arch: z.array(RobotPlanDeviceSliceSchema),
    }),
    /** Failed plans, newest first, at most `failures`. */
    recentFailures: z.array(RobotPlanDeviceFailureSchema),
});
//# sourceMappingURL=robot-plan-admin.js.map